/**
 * Display configuration for the Call Off Management list — status badges,
 * destination icons, ASN stage badges and the stat-card row.
 *
 * Split out of CallOffManagementTab (CR-7717) to keep that component under the
 * 500-line limit. Shared by the list's sub-components.
 *
 * @link CallOffManagementTab for the consumer.
 */
import type { ComponentType } from 'react';
import { Truck, Warehouse, ArrowUpDown } from 'lucide-react';
import type { CallOffStats } from '../../../lib/api';
import { CALL_OFF_STATUS_META, LOGISTICS_CONFIRMED_META } from '../callOffStatus';

type StatusConfigEntry = { icon: ComponentType<{ className?: string }>; color: string; label: string };

// CR-7781: labels and colours come from callOffStatus, shared with the detail,
// the haulier's screens and the export. `logistics_confirmed` is not a
// detail_status — it keys the Logistics Confirmed stat card.
export const STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  ...Object.fromEntries(
    Object.entries(CALL_OFF_STATUS_META).map(([key, meta]) => [
      key, { icon: meta.icon, color: meta.className, label: meta.label },
    ]),
  ),
  logistics_confirmed: {
    icon: LOGISTICS_CONFIRMED_META.icon,
    color: LOGISTICS_CONFIRMED_META.className,
    label: LOGISTICS_CONFIRMED_META.label,
  },
};

export const DESTINATION_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  warehouse: Warehouse,
  direct: Truck,
  mixed: ArrowUpDown,
};

// CR-6291: ASN stage badges shown on the call-off list so the supplier's
// shipment progress (e.g. "ASN Submitted") is visible without drilling into
// the ASN tab. Colours mirror the in-call-off ASN tab. Per request, the
// in-transit badge drops the "ASN" prefix; the rest keep it.
export const ASN_STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  created: { color: 'bg-gray-100 text-gray-700', label: 'ASN Created' },
  submitted: { color: 'bg-blue-100 text-blue-700', label: 'ASN Submitted' },
  in_transit: { color: 'bg-orange-100 text-orange-700', label: 'In Transit' },
  delivered: { color: 'bg-emerald-100 text-emerald-700', label: 'ASN Delivered' },
  received: { color: 'bg-green-100 text-green-700', label: 'ASN Received' },
  cancelled: { color: 'bg-red-100 text-red-700', label: 'ASN Cancelled' },
  // CR-7142: a direct load the customer has acknowledged. Acknowledging records
  // that it left the supplier, but direct stock ships straight to the customer —
  // it never reaches Saltney or Mold, so it must never read "In Transit".
  direct_acknowledged: { color: 'bg-purple-100 text-purple-700', label: 'Direct Acknowledged' },
};

export interface StatCardDef {
  kind: 'status' | 'stage';
  key: string;
  label?: string;
  statKey?: keyof CallOffStats;
  title?: string;
  hideWhenZero?: boolean;
}

// CR-7127: the stat-card row, in order. Six of the seven are call-off
// detail statuses; "In Transit" is the derived shipment stage (the most
// advanced ASN raised against the call-off) and sits straight after Dispatched
// because that is where a load actually goes next. A load in transit is
// counted by In Transit and NOT by Dispatched, so the seven numbers partition
// the list rather than overlapping — see the stats action for the carve-out.
// CR-7142 adds the Direct Acknowledged stage card straight after In Transit —
// the two are the same step of the journey down each path, so they read as a
// pair. It is `hideWhenZero` because a tenant that never ships direct should
// not carry a permanently empty filter: Mark's "if there is none then even this
// filter should not be seen". That makes the row 7 or 8 cards wide, so the grid
// column count is derived rather than hard-coded.
//
// CR-7781 adds Logistics Confirmed straight after Supplier Confirmed: the
// loads among them the haulier has also confirmed. A subset of that card by
// design, not carved out of it — the one pair in the row that overlaps.
export const STAT_CARDS: ReadonlyArray<StatCardDef> = [
  { kind: 'status', key: 'draft' },
  { kind: 'status', key: 'pending' },
  { kind: 'status', key: 'confirmed', title: 'The supplier has confirmed the stock will be ready' },
  {
    kind: 'status',
    key: 'logistics_confirmed',
    title: 'Supplier Confirmed loads the haulier has also confirmed (Confirm Load)',
  },
  { kind: 'status', key: 'dispatched' },
  {
    kind: 'stage',
    key: 'in_transit',
    label: 'In Transit',
    statKey: 'asn_in_transit',
    title: 'Call-offs whose shipment is in transit',
  },
  {
    kind: 'stage',
    key: 'direct_acknowledged',
    label: 'Direct Acknowledged',
    statKey: 'asn_direct_acknowledged',
    title: 'Direct loads acknowledged as left the supplier — these never enter In Transit',
    hideWhenZero: true,
  },
  { kind: 'status', key: 'delivered' },
  { kind: 'status', key: 'cancelled' },
];

/**
 * The list filters behind a status card.
 *
 * One place for the list, its stats card and the export to ask, so the rows,
 * the number and the file cannot disagree about what a card means.
 *
 * @param statusFilter - the status card drilled into ('' = none)
 * @returns query params to merge into the list request
 */
export function statusFilterParams(statusFilter: string): Record<string, string> {
  if (!statusFilter) return {};
  // CR-7781: a stamp beside the status, not a status of its own.
  if (statusFilter === 'logistics_confirmed') {
    return { detail_status: 'confirmed', logistics_confirmed: 'true' };
  }
  // The Dispatched card's number excludes loads that have moved on to In
  // Transit or Direct Acknowledged (each has its own card), so its drill-in
  // has to exclude them too.
  if (statusFilter === 'dispatched') {
    return { detail_status: 'dispatched', asn_stage_exclude: 'in_transit,direct_acknowledged' };
  }
  return { detail_status: statusFilter };
}

/** Which bucket the Active / Completed / Cancelled toggle is showing. */
export type CallOffBucket = 'active' | 'completed' | 'cancelled';

/**
 * CR-9121: the card a haulier's list opens on — the loads it has confirmed
 * and still has to collect — rather than every load it has ever carried. A
 * default, not a scope: the haulier clears it to reach delivered and cancelled
 * loads again, and the server's scope is unchanged.
 */
export const HAULIER_LANDING_FILTER = 'logistics_confirmed';

/**
 * The label of the stat card the list is drilled into.
 *
 * @param statusFilter - the status card drilled into ('' = none)
 * @param asnStageFilter - the shipment-stage card drilled into ('' = none)
 * @returns the card's label, or '' when no card is drilled into
 */
export function drillInLabel(statusFilter: string, asnStageFilter: string): string {
  if (asnStageFilter) {
    const card = STAT_CARDS.find((c) => c.kind === 'stage' && c.key === asnStageFilter);
    return card?.label ?? asnStageFilter;
  }
  if (statusFilter) return STATUS_CONFIG[statusFilter]?.label ?? statusFilter;
  return '';
}

/**
 * Colour class for a call-off's container fill percentage.
 *
 * @param fillPercent - Fill as a percentage (0-100+)
 * @returns Tailwind text classes: red at 90%+, orange at 50%+, green below
 */
export function getFillColorClass(fillPercent: number): string {
  if (fillPercent >= 90) return 'text-red-600 font-semibold';
  if (fillPercent >= 50) return 'text-orange-500 font-semibold';
  return 'text-green-600';
}

export interface SupplierOption {
  id: number;
  name: string;
  supplier_code: string;
}
